import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import { IParentAccountConflict, IParentMaskedCredential } from '../../../lib/types';
import { SwitchAccountButton } from './parent-account-conflict.styles';

interface IParentAccountConflictBannerProps {
  conflicts: IParentAccountConflict[];
  onSwitch: (conflict: IParentAccountConflict, credential: IParentMaskedCredential) => void;
}

// "Jack and Mia" reads better than a bare comma list at these tiny sizes.
const joinNames = (names: string[]): string => {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const describePlayers = (conflict: IParentAccountConflict): string => {
  const named = joinNames(conflict.players.map((player) => player.name));
  const unnamed = conflict.playerCount - conflict.players.length;
  if (!named) return `${conflict.playerCount} player${conflict.playerCount === 1 ? '' : 's'}`;
  if (unnamed > 0) return `${named} and ${unnamed} other${unnamed === 1 ? '' : 's'}`;
  return named;
};

export const ParentAccountConflictBanner: React.FC<IParentAccountConflictBannerProps> = ({
  conflicts,
  onSwitch,
}) => {
  if (!conflicts.length) return null;

  return (
    <>
      {conflicts.map((conflict) => (
        <Alert severity="warning" key={conflict._id} style={{ marginBottom: 12 }}>
          <AlertTitle>Another account has your players</AlertTitle>
          <Typography variant="body2">
            {describePlayers(conflict)}
            {conflict.playerCount === 1 ? ' is ' : ' are '}
            on a separate account that was verified with a different email or phone number.
          </Typography>
          <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {conflict.credentials.map((credential) => (
              <SwitchAccountButton
                key={`${conflict._id}-${credential.key}`}
                size="small"
                variant="contained"
                onClick={() => onSwitch(conflict, credential)}
              >
                {`Move them here using ${credential.masked}`}
              </SwitchAccountButton>
            ))}
          </Box>
        </Alert>
      ))}
    </>
  );
};
